import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { ScrapeJob, Keyword } from '../types';

/** 経過時間を「X分Y秒」形式で返すフック */
function useElapsedTime(createdAt: string | undefined, active: boolean) {
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!active || !createdAt) { setElapsed(''); return; }
    const update = () => {
      const start = new Date(createdAt.endsWith('Z') ? createdAt : createdAt + 'Z');
      const diff  = Date.now() - start.getTime();
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setElapsed(`${m}分${s}秒経過`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [createdAt, active]);
  return elapsed;
}

const Scrape: React.FC = () => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: keywordsData } = useQuery({
    queryKey: ['keywords'],
    queryFn: async () => (await api.get('/keywords')).data,
  });

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['scrape-jobs'],
    queryFn: async () => (await api.get('/scrape')).data,
    refetchInterval: (data: any) => {
      const hasActive = (data?.jobs || []).some(
        (j: any) => j.status === 'running' || j.status === 'queued'
      );
      return hasActive ? 5000 : false;
    },
  });

  const scrapeMutation = useMutation({
    mutationFn: async (keywords: string[]) =>
      (await api.post('/scrape', { keywords })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrape-jobs'] });
      setSelectedKeywords([]);
    },
  });

  const stopMutation = useMutation({
    mutationFn: async (jobId: string) =>
      (await api.delete(`/scrape/${jobId}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrape-jobs'] });
    },
  });

  const keywords: Keyword[] = keywordsData?.keywords?.filter((k: Keyword) => k.enabled) || [];
  const jobs: ScrapeJob[]   = jobsData?.jobs || [];

  const runningJob = jobs.find(j => j.status === 'running' || j.status === 'queued');
  const elapsed    = useElapsedTime(runningJob?.created_at, !!runningJob);

  const toggleKeyword = (kw: string) =>
    setSelectedKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );

  const statusBadge = (status: string) => {
    const map: Record<string, string>    = { queued: 'badge-gray', running: 'badge-blue', completed: 'badge-green', failed: 'badge-red' };
    const labels: Record<string, string> = { queued: '待機中', running: '実行中', completed: '完了', failed: '失敗' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{labels[status] || status}</span>;
  };

  return (
    <div>
      <h1 className="page-title">スクレイピング実行</h1>

      {/* 実行中プログレスカード */}
      {runningJob && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #4f6ef7' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <span style={{ fontWeight: 600, color: '#e2e8f0' }}>
                🕷️ スクレイピング{runningJob.status === 'queued' ? '起動中...' : '実行中'}
              </span>
              <span style={{ marginLeft: 12, fontSize: 13, color: '#94a3b8' }}>{elapsed}</span>
            </div>
            <button
              className="btn btn-secondary"
              style={{ color: '#f87171', borderColor: '#f87171', fontSize: 13 }}
              onClick={() => {
                if (window.confirm('スクレイピングを停止しますか？')) {
                  stopMutation.mutate(runningJob.job_id);
                }
              }}
              disabled={stopMutation.isPending}
            >
              ⏹ 停止
            </button>
          </div>

          {/* インジケータープログレスバー */}
          <div style={{ height: 6, background: '#2d3348', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: '40%',
              background: 'linear-gradient(90deg, #4f6ef7, #7c8ff8)',
              borderRadius: 3,
              animation: 'scrape-progress 1.8s ease-in-out infinite',
            }} />
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            キーワード: {runningJob.keywords?.join('、') || '-'}
          </div>
        </div>
      )}

      {/* プログレスバーアニメーション用スタイル */}
      <style>{`
        @keyframes scrape-progress {
          0%   { margin-left: -40%; }
          100% { margin-left: 100%; }
        }
      `}</style>

      {/* 手動実行カード */}
      <div className="card">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>手動実行</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>実行するキーワードを選択してください</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {keywords.map(kw => (
            <button
              key={kw.keyword_id}
              className={`btn ${selectedKeywords.includes(kw.keyword) ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleKeyword(kw.keyword)}
            >
              {kw.keyword}
            </button>
          ))}
          {keywords.length === 0 && (
            <p style={{ color: '#64748b', fontSize: 13 }}>キーワードが登録されていません</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => scrapeMutation.mutate(selectedKeywords)}
            disabled={selectedKeywords.length === 0 || scrapeMutation.isPending || !!runningJob}
          >
            {scrapeMutation.isPending ? '起動中...' : `🕷️ スクレイピング開始 (${selectedKeywords.length}件)`}
          </button>
          {!!runningJob && (
            <span style={{ fontSize: 13, color: '#f59e0b' }}>※ 実行中のジョブがあります</span>
          )}
        </div>

        {scrapeMutation.isSuccess && (
          <p style={{ marginTop: 12, color: '#48bb78', fontSize: 13 }}>✅ ジョブを起動しました</p>
        )}
      </div>

      {/* ジョブ履歴 */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2d3348' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>ジョブ履歴</h2>
        </div>
        {isLoading ? (
          <div className="loading">読み込み中...</div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🕷️</div>
            <div>ジョブ履歴がありません</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>ジョブID</th>
                <th>キーワード</th>
                <th>状態</th>
                <th>開始日時</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.job_id}>
                  <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
                    {job.job_id.slice(0, 8)}...
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {job.keywords?.map(kw => (
                        <span key={kw} className="badge badge-blue">{kw}</span>
                      ))}
                    </div>
                  </td>
                  <td>{statusBadge(job.status)}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>
                    {job.created_at?.slice(0, 16).replace('T', ' ')}
                  </td>
                  <td>
                    {(job.status === 'running' || job.status === 'queued') && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '2px 8px', color: '#f87171', borderColor: '#f87171' }}
                        onClick={() => {
                          if (window.confirm('このジョブを停止しますか？')) stopMutation.mutate(job.job_id);
                        }}
                        disabled={stopMutation.isPending}
                      >
                        停止
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Scrape;
